import React from 'react';
import { Loader2, Save, FileSpreadsheet, DollarSign } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui/dialog';
import { SwitchToggle } from './SwitchToggle';
import { StockStatusToggles } from './StockStatusToggles';
import { tierLabels } from '../../../../lib/fabricLabels';
import { GEN_ROOM_SCENES, GEN_STYLES } from '../../../../lib/roomTypes';

const PRICING_TIERS = [
  { id: 'blackout', label: 'Blackout — $119 base + $5.50/sqft' },
  { id: 'solar', label: 'Solar / Sunscreen — $99 base + $5.00/sqft' },
  { id: 'light-filtering', label: 'Light Filtering — $89 base + $4.50/sqft' },
  { id: 'sheer', label: 'Sheer — $79 base + $4.00/sqft' },
  { id: 'woven', label: 'Woven / Natural — $129 base + $6.00/sqft' },
];

const TextField = ({ label, value, onChange, placeholder, hint, type = 'text', small, testId }) => (
  <div>
    <Label className={small ? 'text-xs' : ''}>{label}</Label>
    <Input type={type} value={value || ''} onChange={onChange} placeholder={placeholder} className={`mt-1 ${small ? 'text-sm' : ''}`} data-testid={testId} />
    {hint && <p className="text-xs text-neutral-500 mt-1">{hint}</p>}
  </div>
);

export const CollectionDialog = ({ open, onOpenChange, collection, setCollection, brands, onSave, saving }) => {
  const set = (key) => (e) => setCollection(prev => ({ ...prev, [key]: e.target.value }));
  const c = collection || {};
  const brandName = brands.find(b => b.id === c.brand_id)?.name;
  const L = tierLabels(brandName);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="collection-dialog">
        <DialogHeader>
          <DialogTitle>{c.id ? `Edit ${L.tier2}` : `Add New ${L.tier2}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div>
            <Label>Brand *</Label>
            <select value={c.brand_id || ''} onChange={set('brand_id')} className="w-full mt-1 rounded-md border border-neutral-300 p-2" data-testid="collection-brand-select">
              <option value="">Select a brand</option>
              {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <TextField label={`${L.tier2} Name *`} value={c.name} onChange={set('name')} placeholder="e.g., Vivian Collection, 30DN Series" testId="collection-name-input" />
          <div>
            <Label>Description</Label>
            <Textarea value={c.description || ''} onChange={set('description')} placeholder="Description of the collection" className="mt-1" rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Fabric Type" value={c.fabric_type} onChange={set('fabric_type')} placeholder="e.g., Solar Screen, Blackout" />
            <TextField label="Openness" value={c.openness} onChange={set('openness')} placeholder="e.g., 1%, 3%, 5%" />
          </div>

          <div className="border rounded-lg p-3 bg-amber-50/50">
            <Label className="text-sm font-semibold flex items-center gap-1.5 mb-2">
              <DollarSign className="w-4 h-4 text-amber-600" /> Pricing Tier
            </Label>
            <select
              value={c.pricing_tier || ''}
              onChange={set('pricing_tier')}
              className="w-full rounded-md border border-amber-200 p-2 text-sm bg-white"
              data-testid="collection-pricing-tier"
            >
              <option value="">— Auto-detect from name —</option>
              {PRICING_TIERS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <p className="text-xs text-neutral-500 mt-1.5">Sets default pricing for materials in this collection. New syncs will inherit this tier automatically.</p>
          </div>
          <TextField
            label="Collection page on brand website" type="url" value={c.website_url} onChange={set('website_url')}
            placeholder="https://www.brand.com/collections/..." testId="collection-website-url-input"
            hint="Shown as a link when customers enlarge a swatch from this collection"
          />
          <TextField
            label="Original Fabric Colors Catalog Link" type="url" value={c.catalog_url} onChange={set('catalog_url')}
            placeholder="https://view.publitas.com/coulisse-usa/..." testId="collection-catalog-url-input"
            hint="Publitas catalog link with the original brand fabric colors for this collection"
          />
          <TextField
            label="Available Widths (comma-separated)" value={c.widths?.join(', ')}
            onChange={(e) => setCollection(prev => ({ ...prev, widths: e.target.value.split(',').map(w => w.trim()).filter(Boolean) }))}
            placeholder='e.g., 98.4", 118"'
          />

          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-semibold text-neutral-800 mb-3 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" /> Technical Specifications
            </h4>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <TextField small label="Composition" value={c.composition} onChange={set('composition')} placeholder="e.g., 100% Polyester" />
                <TextField small label="Fire Rating" value={c.fire_rating} onChange={set('fire_rating')} placeholder="e.g., NFPA 701, Class A" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TextField small label="Max Width" value={c.width} onChange={set('width')} placeholder="e.g., 118 inches" />
                <TextField small label="Warranty" value={c.warranty} onChange={set('warranty')} placeholder="e.g., 5 year limited" />
              </div>
              <div>
                <Label className="text-xs">Care Instructions</Label>
                <Textarea value={c.care_instructions || ''} onChange={set('care_instructions')} placeholder="e.g., Wipe clean with damp cloth. Do not machine wash." className="mt-1 text-sm h-16" />
              </div>
            </div>
          </div>

          <div className="border-t pt-4 space-y-3">
            <SwitchToggle
              label="Visible Online" hint="Show this collection in frontend configurator"
              checked={c.is_visible_online !== false}
              onToggle={() => setCollection(prev => ({ ...prev, is_visible_online: !prev?.is_visible_online }))}
              testId="collection-visible-toggle"
            />
            <SwitchToggle
              label="Hide Prices Online" hint="Hide prices for this collection even when the brand's prices are shown" onColor="bg-orange-500"
              checked={!!c.hide_prices_online}
              onToggle={() => setCollection(prev => ({ ...prev, hide_prices_online: !prev?.hide_prices_online }))}
              testId="collection-hide-prices-toggle"
            />
            <StockStatusToggles value={c.stock_status || null} onChange={(v) => setCollection(prev => ({ ...prev, stock_status: v }))} discontinued={!!c.discontinued} onDiscontinuedChange={(v) => setCollection(prev => ({ ...prev, discontinued: v }))} testIdPrefix="collection" hint={(o) => `Applies to every color in this ${L.tier2.toLowerCase()} — ${o.label.toLowerCase()}, text on hover`} />
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-neutral-800 mb-3">AI Room Defaults</h4>
            <p className="text-xs text-neutral-500 mb-3">Used for batch generation so every room in this {L.tier2.toLowerCase()} looks consistent.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Default room scene</Label>
                <select value={c.default_room_type || ''} onChange={set('default_room_type')} className="w-full mt-1 rounded-md border border-neutral-300 p-2 text-sm" data-testid="collection-default-room">
                  <option value="">— none —</option>
                  {GEN_ROOM_SCENES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Default style</Label>
                <select value={c.default_style || ''} onChange={set('default_style')} className="w-full mt-1 rounded-md border border-neutral-300 p-2 text-sm" data-testid="collection-default-style">
                  <option value="">— none —</option>
                  {GEN_STYLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="collection-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save {L.tier2}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CollectionDialog;
